import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetIssueUpdatesByParentIssueIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetIssueUpdatesByParentIssueIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC issue data to match the GraphQL query structure
 */
export function mapTrpcIssueUpdatesToGraphQL(
  trpcData: GetIssueUpdatesByParentIssueIdResponseRow[] | undefined
): GetIssueUpdatesByParentIssueIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    issue_update: trpcData,
  };
}

export const useGetIssueUpdateRegister = (issueId: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.issueUpdate.register.queryOptions({
      parentIssueId: issueId,
    }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcIssueUpdatesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
